import { defineStore, storeToRefs } from 'pinia';
import { computed, ref, shallowRef, watch } from 'vue';
import { Contents, EpubCFI, Rendition } from 'epubjs';
import Section from 'epubjs/types/section';
import { getViewerElementWidth, hasWidthForSpreads } from '../util/book';
import { getStoredLocations, storeLocations } from '../util/book-locations-storage';
import type { GoToItem, PageFile, Track } from '../types';
import useSettingStore from './setting';
import useNavigationStore from '../stores/navigation';
import usePositionTrackingStore from './position-tracking';
import useReaderStore from './reader';
import useNavigationTriggerStore from './navigation-trigger';
import { useDisplay } from 'vuetify';
import type { EnhancedPage, EnhancedTocItem } from '../types/navigation';
import { EpubJsDisplayedLocation, EpubJsRendition } from '../types/epubjs';
import Locations from 'epubjs/types/locations';
import useReaderEngine from '../engine/use-reader-engine';

type RenditionState = {
  rendition?: EpubJsRendition;
  isReflowable: boolean;
  hasSpreadNone: boolean;
  isChangingPage: boolean;
  locations?: Locations;
  slideDirection: string;
  currentPageFiles: PageFile[];
  currentPageFilesWithTracks: any;
  currentBookmarkedTrack: any;
  currentTarget?: string;
  isRenderingComplete: boolean;
  hasBackgroundOverride: boolean;
};

const getInitialRenditionState = (): RenditionState => ({
  rendition: undefined,
  isReflowable: false,
  hasSpreadNone: false,
  isChangingPage: false,
  locations: undefined,
  slideDirection: 'left',
  currentPageFiles: [],
  currentPageFilesWithTracks: [],
  currentBookmarkedTrack: null,
  currentTarget: undefined,
  isRenderingComplete: false,
  hasBackgroundOverride: false,
});

const useRenditionStore = defineStore('book-player-rendition', () => {
  const { platform } = useDisplay();
  const hadWidthForSpreadsWhenWeRenderedTheBook = ref(hasWidthForSpreads(platform.value.touch));
  const isAfterReRender = ref(false); // Track if we just re-rendered (from rotation, etc.)

  const { manifest } = useReaderEngine();
  const { fontSize, isPageTurnAnimationEnabled, isReflowablePageSlideAnimationEnabled, isPageDisplayModeSingle } =
    storeToRefs(useSettingStore());
  const { isScrubbing, pages, hasPages } = storeToRefs(useNavigationStore());
  const { setCurrentPageFromCFI, setCurrentPageIndex, getPageIndexByCfi, setCFI } = useNavigationStore();
  const { setNavigationTrigger } = useNavigationTriggerStore();
  const { currentSpineIndex } = storeToRefs(usePositionTrackingStore());
  const { setSpineIndex, setPositionPct } = usePositionTrackingStore();
  const { isContinuous } = storeToRefs(useReaderStore());

  const isInitialRendering = ref(false);
  const renderingKey = ref<string>(new Date().getTime().toString());
  const renditionState = ref<RenditionState>(getInitialRenditionState());

  const autoPageTurnTimerId = ref<number | null>(null);
  const rendition = computed(() => renditionState.value.rendition);
  const isRenderingComplete = computed(() => renditionState.value.isRenderingComplete);
  const locations = computed(() => renditionState.value.locations);
  const currentPageFiles = computed(() => renditionState.value.currentPageFiles);

  const isSinglePageMode = computed(() => renditionState.value.hasSpreadNone || isPageDisplayModeSingle.value);

  const isItemShownOnPage = computed(() => (item: EnhancedTocItem | EnhancedPage) => {
    if (!item.cfi || !rendition.value) return false;

    const location = rendition.value.location;
    if (!location?.start || !location?.end) return false;

    // Strict Boundary Check, Item must be AT or AFTER the top of the screen
    const isAfterStart = compareCFIs(item.cfi, location.start.cfi) >= 0;

    // Item must be BEFORE the bottom of the screen (Strict < instead of <=)
    // If it's exactly at the end, it's actually the first line of the NEXT invisible area.
    const isBeforeEnd = compareCFIs(item.cfi, location.end.cfi) < 0;

    // The "Prevalence" Check (For Continuous Scroll), if the item is in the viewport, it's visible.
    if (isAfterStart && isBeforeEnd) return true;

    // Spanning Logic (Parent/Child check)
    // If we are inside a long chapter, the location CFI will be deeper than the TOC CFI.
    // We check if the item.cfi is a parent of the start location.
    // Only return true if the current location is actually INSIDE this item
    return location.start.cfi.includes(item.cfiBase ?? item.cfi);
  });

  /**
   * Detects if an even spine item is incorrectly positioned on the LEFT side of a spread.
   * This should only trigger after a re-render (rotation/resize) to correct EPUBjs layout issues.
   *
   * Expected Spread Layout:
   * - spine[0] = cover (alone on RIGHT) ✓
   * - spine[1,3,5...] = odd spines (LEFT side) ✓
   * - spine[2,4,6...] = even spines (RIGHT side) ✓
   *
   * Wrong Layout (triggers correction):
   * - spine[2,4,6...] appearing on LEFT side ✗
   *
   * This computed is gated by `isAfterReRender` flag to prevent correction loops.
   * It works in tandem with `adjustTargetForSpreadMode` which prevents the issue during navigation.
   */
  const isRightPageOnLeftSide = computed(() => {
    if (renditionState.value.rendition === null) {
      return false;
    }

    // Block ALL corrections once the flag has been cleared
    // The flag starts as true after re-render, allowing ONE correction
    // Once cleared in handlePaginatedRelocated, this will always return false
    if (!isAfterReRender.value) {
      // Flag is false - either initial load or already corrected after re-render
      // Don't run any spread corrections
      return false;
    }

    // Check if we're in single-page mode (user preference or book requirement)
    // Don't do spread correction if we're in single-page mode
    if (isSinglePageMode.value) {
      return false;
    }

    // Check if the book was ACTUALLY RENDERED with spreads
    // Use hadWidthForSpreadsWhenWeRenderedTheBook instead of current hasWidthForSpreads()
    // This prevents the correction from running during/after re-render before the book stabilizes
    if (!hadWidthForSpreadsWhenWeRenderedTheBook.value) {
      return false;
    }

    const location = renditionState.value.rendition?.location;

    // Detect: Even spine index (2,4,6...) on the LEFT side of a spread
    // - location.start.index < location.end.index = spread is displayed
    // - location.start.index > 0 = not the cover (spine 0 is OK alone)
    // - location.start.index % 2 === 0 = even spine on left (WRONG!)
    return (
      !renditionState.value.isReflowable &&
      location !== undefined &&
      location.start.index < location.end.index &&
      location.start.index > 0 &&
      location.start.index % 2 === 0
    );
  });
  const currentBookmarkedTrack = computed(() => renditionState.value.currentBookmarkedTrack);
  const currentTarget = computed(() => renditionState.value.currentTarget);
  const isChangingPage = computed(() => renditionState.value.isChangingPage);
  const slideDirection = computed(() => renditionState.value.slideDirection);
  const isReflowable = computed(() => renditionState.value.isReflowable);
  const hasSpreadNone = computed(() => renditionState.value.hasSpreadNone);
  const currentPageFilesWithTracks = computed(() => renditionState.value.currentPageFilesWithTracks);
  // Derive isRTL from manifest
  const isRTL = computed(() => manifest.value.direction === 'rtl');

  // Check if we're showing a spread (two pages at once) in fixed layout
  const isSpread = computed(
    () => !isReflowable.value && rendition.value?.location?.start?.index !== rendition.value?.location?.end?.index,
  );

  // Rendition-engine level flags (what EPUB.js reports)
  // epub.js updates rendition.location on its own object, so Vue does not always notice the change (e.g. after a
  // ReflowableSliderManager page turn). This copy is updated on every "relocated" event instead.
  const renditionLocation = shallowRef<EpubJsDisplayedLocation>();
  const setRenditionLocation = (location?: EpubJsDisplayedLocation) => {
    renditionLocation.value = location;
  };
  const isAtRenditionStart = computed(() => !!renditionLocation.value?.atStart);
  const isAtRenditionEnd = computed(() => !!renditionLocation.value?.atEnd);

  // Manager navigation state (for continuous virtual scroller)
  // Only check isSuppressingEvents - this flag protects the full navigation duration (1 second)
  // The isNavigating flag is only for internal prepend anchoring and gets cleared early
  const isManagerNavigating = computed(() => {
    if (!rendition.value?.manager) return false;
    const manager = rendition.value.manager as any;
    return manager.isSuppressingEvents === true;
  });

  const previousActive = computed(() => {
    if (!rendition.value?.location) return false;
    return isRTL.value ? isAtRenditionEnd.value : isAtRenditionStart.value;
  });

  const nextActive = computed(() => {
    if (!rendition.value?.location) return false;
    return isRTL.value ? isAtRenditionStart.value : isAtRenditionEnd.value;
  });

  const setRendition = (_rendition?: any) => {
    renditionState.value.rendition = _rendition as EpubJsRendition;
    setRenditionLocation(_rendition?.location);
    // setRendition can be called more than once with the same rendition.
    _rendition?.off('relocated', setRenditionLocation);
    _rendition?.on('relocated', setRenditionLocation);
  };
  const setLocations = (_locations?: any) => {
    renditionState.value.locations = _locations;
  };
  const setCurrentPageFiles = () => {
    if (!renditionState.value.rendition?.location) return;
    renditionState.value.currentPageFiles = Array.from(
      new Set([renditionState.value.rendition.location.start, renditionState.value.rendition.location.end]),
    );
  };
  const setCurrentPageFilesWithTracks = (_currentPageFilesWithTracks: any) => {
    renditionState.value.currentPageFilesWithTracks = _currentPageFilesWithTracks;
  };
  const setIsChangingPage = (_isChangingPage: boolean) => {
    renditionState.value.isChangingPage = _isChangingPage;
  };
  const setIsReflowable = (_isReflowable: boolean) => {
    renditionState.value.isReflowable = _isReflowable;
  };
  const setHasSpreadNone = (_hasSpreadNone: boolean) => {
    renditionState.value.hasSpreadNone = _hasSpreadNone;
  };
  const setHasBackgroundOverride = (_hasBackgroundOverride: boolean) => {
    renditionState.value.hasBackgroundOverride = _hasBackgroundOverride;
  };
  const setCurrentBookmarkedTrack = (_currentBookmarkedTrack: Track) => {
    renditionState.value.currentBookmarkedTrack = _currentBookmarkedTrack;
  };
  /**
   * Sets a temporary navigation target (WHERE we're navigating TO).
   * This is different from position-tracking's currentCFI which tracks WHERE we currently ARE.
   * Used during navigation to track the destination before the navigation completes.
   */
  const setCurrentTarget = (_currentTarget: string) => {
    renditionState.value.currentTarget = _currentTarget;
  };

  const getCurrentTarget = () => renditionState.value.currentTarget;
  const clearCurrentTarget = () => {
    renditionState.value.currentTarget = undefined;
  };

  const $reset = () => {
    renditionState.value = getInitialRenditionState();
    renditionLocation.value = undefined;
    isInitialRendering.value = false;
    isAfterReRender.value = false;
  };

  const resetState = () => {
    $reset();
  };

  /**
   * To be used only for Right to Left image books.
   */
  const goToNextPage = () => {
    if (!renditionState.value.rendition) return;

    const nextPage = getSectionByTarget(currentSpineIndex.value + 1);

    if (nextPage && nextPage.href) {
      safeDisplay(nextPage.href).then(() => {
        setCurrentPercentageFromPage();
        setCurrentPageFiles();
      });
    }
  };

  const clearAutoPageTurnTimer = () => {
    if (autoPageTurnTimerId.value) {
      clearTimeout(autoPageTurnTimerId.value);
    }
  };

  const doWithAutoPageTurnTimeout = (callback: () => void, delay: number) => {
    clearAutoPageTurnTimer();
    autoPageTurnTimerId.value = setTimeout(() => {
      callback();
    }, delay) as unknown as number;
  };

  const goToPreviousPage = () => {
    if (!renditionState.value.rendition) return;

    const previousPage = getSectionByTarget(currentSpineIndex.value - 1);

    if (previousPage && previousPage.href) {
      safeDisplay(previousPage.href).then(() => {
        setCurrentPercentageFromPage();
        setCurrentPageFiles();
      });
    }
  };

  /**
   * ReflowableSliderManager ignores page turns while one is in progress. We skip them here too, so an ignored turn does
   * not change the store (e.g. slideDirection, which is reported as the navigation direction).
   */
  const isReflowablePageTurnInProgress = () =>
    isReflowablePageSlideAnimationEnabled.value && !!renditionState.value.rendition?.manager?.isTurningPage;

  const goToPreviousSpine = () => {
    const rendition = renditionState.value?.rendition;
    if (!rendition?.location) return;
    if (isReflowablePageTurnInProgress()) return;

    if (isAtRenditionStart.value) {
      console.debug('The beginning of the book has been reached');
      return;
    }

    // TODO - remove page slide animation if we have page turning animation
    clearAutoPageTurnTimer();
    renditionState.value.isChangingPage = true;
    renditionState.value.slideDirection = isRTL.value ? 'left' : 'right';

    rendition.prev().then(() => {
      // Ensure store is updated after navigation
      setCurrentPercentageFromPage();
      setCurrentPageFiles();
    });
  };

  const goToNextSpine = () => {
    const rendition = renditionState.value?.rendition;
    if (!rendition?.location) return;
    if (isReflowablePageTurnInProgress()) return;

    if (isAtRenditionEnd.value) {
      console.debug('The end of the book has been reached');
      return;
    }

    // TODO - remove page slide animation if we have page turning animation
    clearAutoPageTurnTimer();
    renditionState.value.isChangingPage = true;
    renditionState.value.slideDirection = isRTL.value ? 'right' : 'left';

    if (isRTL.value && !isReflowable.value && !isPageTurnAnimationEnabled.value) {
      /** RTL Fixed layout epub spread mode rendition.next() is broken in epubjs latest version,
       * So bellow is the work around for that problem. The bellow workaround can be removed once
       * the above problem will be fixed in future version of epubjs.
       *
       * For books with page turn animation, we can use rendition.next() directly because it is overwritten in the display manager.
       * */
      goToNextPage();
      return;
    }

    rendition.next().then(() => {
      // Ensure store is updated after navigation
      setCurrentPercentageFromPage();
      setCurrentPageFiles();
    });
  };

  /**
   * Gets the spine index from a target (CFI, href, or numeric index).
   * Uses EPUBjs's native CFI parsing when available, otherwise falls back to spine.get().
   */
  const getSpineIndexFromTarget = (target: string | number): number | null => {
    if (!renditionState.value.rendition?.book?.spine) return null;

    try {
      // If it's a CFI string, use EpubCFI.spinePos for direct spine index extraction
      if (typeof target === 'string' && new EpubCFI().isCfiString(target)) {
        const parsedCfi = new EpubCFI(target);
        // spinePos gives us the spine index directly from the CFI
        return parsedCfi.spinePos ?? null;
      }

      // For hrefs or numeric indices, use spine.get()
      const section = renditionState.value.rendition.book.spine.get(target);
      return section?.index ?? null;
    } catch (error) {
      console.warn('Failed to get spine index from target:', target, error);
      return null;
    }
  };

  /**
   * Adjusts navigation target for spread mode to prevent even spine items from appearing on the left.
   *
   * Spine Structure for Fixed-Layout Books in Spread Mode:
   * - spine[0] = cover (displayed alone on RIGHT)
   * - spine[1] = odd page (LEFT side of spread) ✓
   * - spine[2] = even page (RIGHT side of spread) ✓
   * - spine[3] = odd page (LEFT side of spread) ✓
   * - spine[4] = even page (RIGHT side of spread) ✓
   * ...
   *
   * Problem: Navigating to spine[2,4,6...] can result in them appearing on the LEFT instead of RIGHT.
   * Solution: Redirect to the previous spine[1,3,5...] so the spread displays correctly.
   *
   * NOTE: This should ONLY apply to paginated fixed-layout books, NOT continuous scrolling books.
   */
  const adjustTargetForSpreadMode = (target: string): string => {
    if (isContinuous.value) {
      return target;
    }

    if (!isReflowable.value && !isSinglePageMode.value && hasWidthForSpreads(platform.value.touch)) {
      // Use spine index to determine if adjustment is needed
      const targetSpineIndex = getSpineIndexFromTarget(target);

      if (targetSpineIndex !== null && targetSpineIndex > 0 && targetSpineIndex % 2 === 0) {
        // Even spine index (2,4,6...) would appear on LEFT side - redirect to previous odd spine
        const previousSection = renditionState.value.rendition?.book?.spine?.get(targetSpineIndex - 1);

        if (previousSection) {
          // Always return href - constructing CFIs can cause EPUBjs errors
          return previousSection.href;
        }
      }
    }
    return target;
  };

  const goToPage = ({ target, isGoingForwardsInBook }: GoToItem): Promise<void> => {
    return new Promise((resolve, reject) => {
      if (!renditionState.value.rendition) {
        console.debug('goToPage: rendition not ready, skipping');
        return resolve();
      }

      let sanitizedTarget: any = target;

      if (typeof target === 'string') {
        sanitizedTarget = target.trim();
        if (!sanitizedTarget) return reject(new Error('Empty target'));
        setCurrentTarget(sanitizedTarget);
        // Adjust for spread mode if needed
        sanitizedTarget = adjustTargetForSpreadMode(sanitizedTarget);
      }

      // Set Slide Direction
      renditionState.value.slideDirection = isRTL.value
        ? isGoingForwardsInBook
          ? 'right'
          : 'left'
        : isGoingForwardsInBook
          ? 'left'
          : 'right';

      renditionState.value.isChangingPage = true;

      return renditionState.value.rendition
        .display(sanitizedTarget)
        .then(() => {
          // DON'T reset isChangingPage here - let onLocationChanged handle it
          // This ensures the flag is still true when onLocationChanged checks it
          // renditionState.value.isChangingPage = false;
          resolve();
        })
        .catch(async (err: Error) => {
          if (typeof sanitizedTarget === 'string' && sanitizedTarget.includes('#') && renditionState.value.rendition) {
            const [base] = sanitizedTarget.split('#');
            await safeDisplay(base);
            // DON'T reset here either - let onLocationChanged handle it
            // renditionState.value.isChangingPage = false;
            resolve();
          } else if (!renditionState.value.rendition?.book?.spine) {
            // Book was destroyed mid-display (hooks.content / spine teardown race) — swallow silently.
            console.warn('Display cancelled — book destroyed during navigation:', sanitizedTarget);
          } else {
            renditionState.value.isChangingPage = false; // Only reset on actual error
            reject(err);
          }
        });
    });
  };

  const setCurrentPercentageFromPage = () => {
    const rendition = renditionState.value.rendition;
    const location = rendition?.location;

    // Guard: Don't update the store's position if the user is currently scrubbing.
    // This prevents the slider from "jumping" while the user holds the thumb.
    if (!location?.start || isScrubbing.value) {
      return;
    }

    const startCfi = location.start.cfi;

    // ALWAYS update spine index and CFI from location
    setSpineIndex(location.start.index);
    setCFI(startCfi);

    let calculatedPercentage = 0;

    if (hasPages.value && pages.value.length > 0) {
      const totalPages = pages.value.length;

      // Find the current page index by comparing CFIs
      const pageIdx = getPageIndexByCfi(startCfi, compareCFIs);

      if (pageIdx !== -1) {
        // Set the percentage based on actual page progress
        // This stops the "Jump to 0" or "Jump to Chapter Start" bug
        calculatedPercentage = (pageIdx / (totalPages - 1)) * 100;

        // Also update the navigation store's currentPageIndex to keep them in sync
        setCurrentPageIndex(pageIdx);
      }
    } else if (isReflowable.value) {
      if (renditionState.value.locations) {
        const pct = renditionState.value.locations.percentageFromCfi(startCfi);
        if (!isNaN(pct)) {
          calculatedPercentage = pct * 100;
        }
      } else if (rendition && rendition.book.spine) {
        // Chapter-based percentage (Spine Index / Total Spine)
        // This keeps the slider moving even before locations.generate() finishes
        const totalSpine = rendition.book.spine.length;
        calculatedPercentage = (location.start.index / totalSpine) * 100;
      }
    } else {
      // Fixed-layout book without page list - use spine index directly
      calculatedPercentage = location.start.index;
    }

    // Update position percentage in position tracking store (single source of truth)
    setPositionPct(calculatedPercentage);

    // Always try to sync the page index from CFI if pages are available
    if (hasPages.value && pages.value.length > 0) {
      setCurrentPageFromCFI(startCfi, compareCFIs);
    }
  };

  const goToPageFromBookMark = ({
    target,
    isGoingForwardsInBook,
    bookmarkedTrack,
  }: {
    target: any;
    isGoingForwardsInBook: boolean;
    bookmarkedTrack: any;
  }) => {
    if (!renditionState.value.rendition) return;
    clearAutoPageTurnTimer();
    renditionState.value.slideDirection = isGoingForwardsInBook ? 'left' : 'right';
    renditionState.value.isChangingPage = true;
    setCurrentBookmarkedTrack(bookmarkedTrack);
    safeDisplay(target).then(() => {
      // Ensure store is updated after navigation
      setCurrentPercentageFromPage();
      setCurrentPageFiles();
    });
  };

  const destroyBook = () => {
    if (rendition.value?.book?.destroy) {
      rendition.value?.book?.destroy();
    }
  };

  watch(fontSize, (fontSizeEM) => {
    if (!rendition.value?.themes) {
      return;
    }

    rendition.value.themes.fontSize(`${fontSizeEM}em`);
    refreshLayout();
  });

  const setFontSize = (fontSizeEM: number) => {
    if (!rendition.value?.themes) {
      return;
    }

    rendition.value.themes.fontSize(`${fontSizeEM}em`);
    refreshLayout();
  };

  const resetFontSize = () => {
    setFontSize(fontSize.value);
  };

  const isRenditionInitialized = computed(() => !!rendition.value && isRenderingComplete.value);

  const currentRenditionLocation = computed((): EpubJsDisplayedLocation | undefined => {
    if (!rendition.value) return undefined;
    try {
      return rendition.value.currentLocation() as unknown as EpubJsDisplayedLocation;
    } catch {
      return undefined;
    }
  });

  const initializeRendition = (_rendition: Rendition) => {
    setRendition(_rendition);
    // Record whether the book was ACTUALLY rendered with spreads
    // This depends on: screen width AND user preference AND book requirements
    hadWidthForSpreadsWhenWeRenderedTheBook.value = hasWidthForSpreads(platform.value.touch) && !isSinglePageMode.value;
  };

  const setFontType = (fontType: string) => {
    if (!rendition.value?.themes) {
      return;
    }

    rendition.value.themes.font(fontType);
    refreshLayout();
  };

  /**
   * Refresh the layout of the current page
   */
  const refreshLayout = () => {
    if (!rendition.value || !rendition.value.manager) {
      return;
    }
    rendition.value.manager.updateLayout();
  };

  // Callbacks registered by the view layer to reset cross-store state before re-rendering.
  // Avoids store-to-store coupling.
  const beforeReRenderCallbacks: Array<() => void> = [];

  const registerBeforeReRenderCallback = (cb: () => void): (() => void) => {
    beforeReRenderCallbacks.push(cb);
    return () => {
      const idx = beforeReRenderCallbacks.indexOf(cb);
      if (idx > -1) beforeReRenderCallbacks.splice(idx, 1);
    };
  };

  const forceReRendering = () => {
    console.log('Force re-rendering the book');
    destroyBook();
    $reset();
    beforeReRenderCallbacks.forEach((cb) => cb());

    // Update hadWidthForSpreadsWhenWeRenderedTheBook BEFORE re-rendering
    // This ensures renderBook() chooses the correct manager (FlipperManager vs DefaultManager)
    hadWidthForSpreadsWhenWeRenderedTheBook.value = hasWidthForSpreads(platform.value.touch) && !isSinglePageMode.value;

    // Set flag to disable spread correction after re-render
    // This prevents navigation loops when rotating from portrait to landscape
    isAfterReRender.value = true;

    // The relocation this causes is a rendering artifact (rotation, resize, display-mode
    // change), not the reader moving through the book — label it so it doesn't sit in the
    // untriggered bucket alongside genuinely untracked navigation.
    setNavigationTrigger('RE_RENDERING');

    renderingKey.value = new Date().getTime().toString();
  };

  const pendingResize = ref<{ width: string | number; height: string | number } | null>(null);
  /**
   * Resize the rendition
   *
   * @param width percentage or number. e.g. '100%' or 320
   * @param height percentage or number. e.g. '100%' or 320
   */
  const resize = (width: string | number, height: string | number) => {
    if (!rendition.value) {
      console.debug('Rendition not initialized, can not resize');
      pendingResize.value = { width, height };
      return;
    }

    if (!renditionState.value.isRenderingComplete) {
      console.debug('Rendering not complete, queuing resize');
      pendingResize.value = { width, height };
      return;
    }

    // Clear any pending resize request now that we're executing a resize
    pendingResize.value = null;

    const hasWidthForSpreadsAfterThisResize = hasWidthForSpreads(platform.value.touch);

    // Only trigger re-render if we're NOT already in a forced single-page mode (from user preference)
    // If user chose single page mode, don't re-render just because screen is wide enough for spreads
    if (hadWidthForSpreadsWhenWeRenderedTheBook.value != hasWidthForSpreadsAfterThisResize && !isSinglePageMode.value) {
      forceReRendering();
      return;
    }

    // Sometimes we get percentages
    const widthInPX = typeof width === 'string' ? (parseInt(width, 10) / 100) * getViewerElementWidth() : width;

    // @ts-ignore it supports percentages
    rendition.value.resize(widthInPX, height);
  };

  const displayOnePage = computed(() => !hadWidthForSpreadsWhenWeRenderedTheBook.value);

  // TODO - find better name for this function. Unclear what it does
  const handleBookLocations = async (forceGenerate = false) => {
    if (!rendition.value || !rendition.value.book) {
      console.log('Rendition or book not initialized, can not handle book locations');
      return;
    }

    await rendition.value.book.ready;

    const storageKey = rendition.value.book.key ? `${rendition.value.book.key()}-locations` : 'book-locations';

    try {
      const stored = await getStoredLocations(storageKey);
      if (!!stored && !forceGenerate && rendition.value.book.locations?.load) {
        rendition.value.book.locations.load(stored);
      } else if (rendition.value.book.locations?.generate) {
        await rendition.value.book.locations.generate(1024); // chars per location
      }

      // TODO -  This might be redundant because we store in the store's state something that we can already access from the rendition
      setLocations(rendition.value.book.locations);

      if (rendition.value.book.locations?.save) {
        await storeLocations(storageKey, rendition.value.book.locations.save());
      }
    } catch (err) {
      console.error("Couldn't retrieve the list of locations for a reflowable book", err);
    }
  };

  const getSectionByTarget = (target: string | number): Section | undefined => {
    if (!rendition.value || !rendition.value.book.spine) return undefined;
    return rendition.value.book.spine.get(target);
  };

  // Pre-validates that the spine contains the target before calling rendition.display().
  // Prevents "No Section Found" rejections and downstream hooks.content crashes.
  // Catches the race where section.destroy() clears hooks mid-load.
  const safeDisplay = async (target: string | number): Promise<void> => {
    const r = renditionState.value.rendition;

    if (!r?.book?.spine) return;

    if (!r.book.spine.get(target)) {
      console.warn('No section for target, skipping:', target);
      return;
    }

    try {
      if (typeof target === 'number') {
        await r.display(target);
      } else {
        await r.display(target);
      }
    } catch (err: any) {
      // If the spine is gone by the time display() fails, the book was destroyed mid-flight.
      if (!r?.book?.spine) {
        console.warn('Display cancelled — book destroyed during load:', target);
      } else {
        // Re-throw if it's a genuine rendering failure while the book is active
        throw err;
      }
    }
  };

  const getCFIFromPercentage = (index: number): string | null => {
    if (!rendition.value) return null;

    if (isReflowable.value) {
      const locations = renditionState.value.locations;
      if (!locations) return null;

      const cfiString = locations.cfiFromPercentage(index / 100);
      if (!cfiString) return null;

      return new EpubCFI(cfiString).toString();
    }
    if (!rendition.value.book.spine) return null;
    const section = rendition.value.book.spine.get(index) as Section;
    return section?.cfiBase || null;
  };

  const addSelectionListener = (callback: (cfiRange: string, contents: Contents) => void) => {
    if (!rendition.value) return;
    rendition.value.on('selected', callback);
  };

  const removeSelectionListener = (callback: (cfiRange: string, contents: Contents) => void) => {
    if (!rendition.value) return;
    rendition.value.off('selected', callback);
  };

  const setIsRenderingComplete = () => {
    renditionState.value.isRenderingComplete = true;
    // DON'T reset isAfterReRender here - it needs to stay true throughout the session
    // The flag is only reset in $reset() for fresh book loads
  };

  /**
   * Clears the after-re-render flag
   * Called after the first relocated event following a re-render
   * This allows ONE correction to run, then prevents subsequent corrections
   */
  const clearAfterReRenderFlag = () => {
    if (isAfterReRender.value) {
      console.debug('Clearing isAfterReRender flag - future corrections will be blocked');
      isAfterReRender.value = false;
    }
  };

  const sanitizeCfi = (cfi: string | EpubCFI): string | EpubCFI => {
    if (typeof cfi !== 'string') return cfi;

    // Use regex to find the part that starts with epubcfi( and ends with )
    const match = cfi.match(/epubcfi\([^)]+\)/);
    return match ? match[0] : cfi.trim();
  };

  const compareCFIs = (cfiOne: string | EpubCFI, cfiTwo: string | EpubCFI): number => {
    if (!rendition.value?.epubcfi || !rendition.value.epubcfi.compare) return 0;

    const cleanOne = sanitizeCfi(cfiOne);
    const cleanTwo = sanitizeCfi(cfiTwo);

    try {
      return rendition.value.epubcfi.compare(cleanOne, cleanTwo);
    } catch {
      console.warn('CFI Comparison failed', { cleanOne, cleanTwo });
      return 0;
    }
  };

  const hasBackgroundOverride = computed(() => renditionState.value.hasBackgroundOverride);

  return {
    currentTarget,
    isRenditionInitialized,
    currentBookmarkedTrack,
    currentPageFiles,
    isChangingPage,
    isItemShownOnPage,
    isReflowable,
    hasSpreadNone,
    isRightPageOnLeftSide,
    isSpread,
    locations,
    slideDirection,
    nextActive,
    previousActive,
    currentPageFilesWithTracks,
    isRTL,
    rendition,
    renderingKey,
    isInitialRendering,
    displayOnePage,
    pendingResize,
    currentRenditionLocation,
    isAtRenditionStart,
    isAtRenditionEnd,
    isManagerNavigating,
    hadWidthForSpreadsWhenWeRenderedTheBook,
    hasBackgroundOverride,
    goToNextSpine,
    goToNextPage,
    goToPage,
    goToPageFromBookMark,
    goToPreviousPage,
    goToPreviousSpine,
    resetState,
    setCurrentBookmarkedTrack,
    setCurrentPageFiles,
    setCurrentPageFilesWithTracks,
    setCurrentPercentageFromPage,
    setIsChangingPage,
    setIsReflowable,
    setHasSpreadNone,
    setHasBackgroundOverride,
    setLocations,
    setRendition,
    doWithAutoPageTurnTimeout,
    setFontSize,
    initializeRendition,
    resetFontSize,
    setFontType,
    resize,
    destroyBook,
    handleBookLocations,
    getSectionByTarget,
    setCurrentTarget,
    getCurrentTarget,
    clearCurrentTarget,
    addSelectionListener,
    removeSelectionListener,
    forceReRendering,
    registerBeforeReRenderCallback,
    refreshLayout,
    setIsRenderingComplete,
    clearAfterReRenderFlag,
    getCFIFromPercentage,
    compareCFIs,
  };
});

export default useRenditionStore;
